import { Product, ProductVariant } from '@/types'
import { OPTION_TYPE_SIZE } from '@/utils/constants'
import { FREE_UPSIZE_MAX_DISCOUNT_PERCENT } from '@/utils/constants/discount'

// Tìm giá size liền dưới (cùng các option còn lại) để tính Free Upsize cho variant được chọn.
// Thứ tự size lấy theo thứ tự values của option size trên sản phẩm. Duyệt từ size liền kề xuống,
// bỏ qua size có giá không thấp hơn, lấy size đầu tiên có giá thấp hơn.
// Chỉ đủ điều kiện khi (giá variant - giá size nhỏ) / giá variant <= FREE_UPSIZE_MAX_DISCOUNT_PERCENT (tính theo cent).
// Trả về giá size nhỏ nếu đủ điều kiện, ngược lại null.
export function getFreeUpsizeBasePrice(
  product: Product | undefined | null,
  variant: ProductVariant | undefined | null
): number | null {
  try {
    return calcFreeUpsizeBasePrice(product, variant)
  } catch (error) {
    // Lỗi thì coi như không đủ điều kiện, không ảnh hưởng luồng cũ
    return null
  }
}

function calcFreeUpsizeBasePrice(
  product: Product | undefined | null,
  variant: ProductVariant | undefined | null
): number | null {
  if (!product || !variant) return null

  const currentPrice = Number(variant.priceV2?.amount || 0)
  if (!currentPrice) return null

  const sizeOption = variant.selectedOptions?.find(
    (opt) => opt.type === OPTION_TYPE_SIZE
  )
  const sizeValues = product.options?.find(
    (opt) => opt.name === sizeOption?.name
  )?.values
  if (!sizeOption || !sizeValues) return null

  for (let i = sizeValues.indexOf(sizeOption.value) - 1; i >= 0; i--) {
    const smallerVariant = product.variants?.find((v) =>
      v.selectedOptions?.every((opt) =>
        opt.name === sizeOption.name
          ? opt.value === sizeValues[i]
          : variant.selectedOptions?.some(
              (c) => c.name === opt.name && c.value === opt.value
            )
      )
    )
    const smallerPrice = Number(smallerVariant?.priceV2?.amount || 0)
    if (smallerPrice && smallerPrice < currentPrice) {
      const currentCents = Math.round(currentPrice * 100)
      const smallerCents = Math.round(smallerPrice * 100)
      const isEligible =
        (currentCents - smallerCents) * 100 <=
        FREE_UPSIZE_MAX_DISCOUNT_PERCENT * currentCents
      return isEligible ? smallerPrice : null
    }
  }
  return null
}

// Danh sách giá trị size được Free Upsize (giữ nguyên các option khác của variant đang chọn).
export function getFreeUpsizeEligibleSizes(
  product: Product | undefined | null,
  selectedVariant: ProductVariant | undefined | null
): string[] {
  try {
    return calcFreeUpsizeEligibleSizes(product, selectedVariant)
  } catch (error) {
    // Lỗi thì coi như không có size nào đủ điều kiện
    return []
  }
}

function calcFreeUpsizeEligibleSizes(
  product: Product | undefined | null,
  selectedVariant: ProductVariant | undefined | null
): string[] {
  if (!product || !selectedVariant) return []

  const sizeOption = selectedVariant.selectedOptions?.find(
    (opt) => opt.type === OPTION_TYPE_SIZE
  )
  const sizeValues = product.options?.find(
    (opt) => opt.name === sizeOption?.name
  )?.values
  if (!sizeOption || !sizeValues) return []

  return sizeValues.filter((size) => {
    const variantOfSize = product.variants?.find((v) =>
      v.selectedOptions?.every((opt) =>
        opt.name === sizeOption.name
          ? opt.value === size
          : selectedVariant.selectedOptions?.some(
              (c) => c.name === opt.name && c.value === opt.value
            )
      )
    )
    return getFreeUpsizeBasePrice(product, variantOfSize) !== null
  })
}
